import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { CoinPriceTextFormat } from "@lib/components/MiniComponents/Others";
import useScreen from "@lib/hooks/useScreen";
import {
  TransactionType,
  TransactionProxyState,
  TransactionTypeLog,
  TransactionTypeLogType,
  ProviderTransactionConfigConfig,
} from "@lib/types";
import { Descriptions, Typography } from "antd";
import { useIntl } from "react-intl";

type Props = {
  transaction?: TransactionType;
  createTransactionLog: (
    data: Pick<TransactionTypeLog, "transactionId" | "comment" | "type">,
  ) => void;
};

export default function PaymentInstructions({
  transaction,
  createTransactionLog,
}: Props) {
  const { messages } = useIntl();
  const { isMobile } = useScreen();

  const { config } = transaction?.providerTransactionConfig || {};

  const handleCopy = (comment: string) => {
    createTransactionLog({
      comment: `Copied ${comment}`,
      transactionId: transaction.id,
      type: TransactionTypeLogType.Copied,
    });
  };

  const handleCopyAll = () => {
    const allItems = Object.entries({
      amount: `${transaction.amount} ${transaction.currency}`,
      ...config,
    })
      .filter(([key]) => key !== "type")
      .map(
        ([key, value]) =>
          `${messages[`common.${key}` as string] || key}: ${value}`,
      )
      .join("\n");
    createTransactionLog({
      comment: allItems,
      transactionId: transaction.id,
      type: TransactionTypeLogType.Copied,
    });
    navigator.clipboard.writeText(allItems);
  };

  const ItemComponent = ({ itemKey }: { itemKey: string }) => {
    const item = config?.[itemKey as keyof ProviderTransactionConfigConfig];

    return (
      <Typography.Text
        copyable={{ text: item, onCopy: () => handleCopy(itemKey) }}
        ellipsis
      >
        {item}
      </Typography.Text>
    );
  };

  if (
    !transaction?.providerTransactionConfigId ||
    transaction?.state !== TransactionProxyState.PendingCustomerApproval
  ) {
    return null;
  }

  return (
    <Descriptions
      bordered
      column={isMobile ? 1 : 2}
      style={{ width: "100%" }}
      size="small"
    >
      <Descriptions.Item label={messages["common.copyAll"] as string} span={2}>
        <ActionButton
          type="copy"
          iconPosition="right"
          label={messages["common.copyToPaymentInstructions"] as string}
          onClick={() => handleCopyAll()}
        />
      </Descriptions.Item>

      <Descriptions.Item label={messages["common.amount"] as string}>
        <CoinPriceTextFormat
          amount={transaction.amount}
          showCurrency={transaction.currency}
          showCurrencyImage
          align="left"
        />
      </Descriptions.Item>

      {config?.type === "card" && (
        <>
          <Descriptions.Item label={messages["common.cardNumber"] as string}>
            <ItemComponent itemKey="cardNumber" />
          </Descriptions.Item>
          <Descriptions.Item label={messages["common.cardCvv"] as string}>
            <ItemComponent itemKey="cardCvv" />
          </Descriptions.Item>
          <Descriptions.Item
            label={messages["common.cardHolderName"] as string}
          >
            <ItemComponent itemKey="cardHolderName" />
          </Descriptions.Item>
          <Descriptions.Item label={messages["common.cardExpiry"] as string}>
            <ItemComponent itemKey="cardExpiry" />
          </Descriptions.Item>
        </>
      )}

      {config?.type === "bank_account" && (
        <>
          <Descriptions.Item label={messages[`common.bankName`] as string}>
            <ItemComponent itemKey="bankName" />
          </Descriptions.Item>
          <Descriptions.Item label={messages[`common.accountNumber`] as string}>
            <ItemComponent itemKey="accountNumber" />
          </Descriptions.Item>
          <Descriptions.Item label={messages[`common.accountName`] as string}>
            <ItemComponent itemKey="accountName" />
          </Descriptions.Item>
        </>
      )}
    </Descriptions>
  );
}
